import { Editor } from "@monaco-editor/react";
import {
  Button,
  Grid,
  Paper,
  Box,
  AppBar,
  Toolbar,
  Typography,
  Tabs,
  Tab,
  Tooltip,
  IconButton,
} from "@mui/material";
import { ClangJS } from "./clangjs";
import PropTypes from "prop-types";
import { useState, useRef, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ExpandLess,
  ExpandMore,
  HelpOutline,
  PlayArrow,
} from "@mui/icons-material";
import * as d3 from "d3";
import { graphviz } from "d3-graphviz";
import "./App.css";
import { Terminal } from "xterm";
import { FitAddon } from "xterm-addon-fit";
import "xterm/css/xterm.css";
import { LoadingButton } from "@mui/lab";
import Toolbox from "./Toolbox";

function RightTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`right-tabpanel-${index}`}
      aria-labelledby={`right-tab-${index}`}
      style={{
        flexGrow: 1,
        display: value !== index ? "none" : "flex",
        flexFlow: "column",
      }}
      {...other}
    >
      {children}
    </div>
  );
}

RightTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

const defaultCode = `#include<bits/stdc++.h>
using namespace std;
#define int long long
#include "voi.h"
struct Trie{
	static const int MAXN = 2e5 + 10;
	int ch[MAXN][26],tot=1,root=1;
	string str;
	
	void dfs(int &u,int dep,int fa){
		if(!u){u = ++tot;add_node(u,false);add_edge(fa,u,true,false);set_edge_label(fa,u,str[dep-1]);}		set_node_color(u,"green");
		if(dep == str.size()) {set_node_color(u,"white"); return;}
		int w = str[dep] - 'a';
		dfs(ch[u][w],dep+1,u);set_node_color(u,"white");
	}
	
	void push(string s){
		str = s;
		dfs(root,0,0);
	}
}trie;

signed main(){
	voi_init();
  add_node(1);
	int N;
	cin>>N;
	string str;
	for(int i = 1;i <= N; i++){
		cin>>str;
		trie.push(str);
	}
}`;
let realPageNo = 0;
let stdout = "";
let graph = "";
let frames = [];
let decoration = null;
let term = null;

function App() {
  const codeEditorRef = useRef(null);
  const monacoRef = useRef(null);
  const inputEditorRef = useRef(null);
  const stdoutEditorRef = useRef(null);
  const terminalRef = useRef(null);
  const [pageNo, setPageNo] = useState(0);
  const [running, setRunning] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [tabNo, setTabNo] = useState(0);
  const [inputPanelOpen, setInputPanelOpen] = useState(false);

  useEffect(() => {
    d3.select("#canvas").graphviz().width("100%").height("100%").engine("dot");
    if (term === null) {
      term = new Terminal({ convertEol: true });
      const fitAddon = new FitAddon();
      term.loadAddon(fitAddon);
      term.open(terminalRef.current);
      fitAddon.fit();
      term.write("编译器输出和标准错误输出将会打印在此\r\n");
    }
  }, []);

  useEffect(() => {
    if (codeEditorRef.current && inputPanelOpen === true) {
      console.log("fuck");
      window.requestAnimationFrame(() => codeEditorRef.current.layout());
    }
  }, [inputPanelOpen]);

  const writeCallback = ({ fd, str }) => {
    if (fd === 1) {
      stdout += str;
      stdoutEditorRef.current.setValue(stdout);
    } else if (fd === 2) {
      term.write(str);
    } else if (fd === 3) {
      graph += str;
      console.log(`write to fd=${fd}: ${str}`);
    } else if (fd === 4) {
      onGenerateFramesClick();
      setTabNo(1);
    } else if (fd === 5) {
      setRunning(false);
    }
  };

  const runCode = async () => {
    setRunning(true);
    setTabNo(0);
    term.clear();
    term.write("编译器输出和标准错误输出将会打印在此\r\n");
    stdout = "";
    graph = "";
    const clang = new ClangJS(window.location + "/clang", writeCallback);
    await clang.onReady;
    clang.run(
      codeEditorRef.current.getValue(),
      inputEditorRef.current.getValue(),
      "code",
    );
  };

  const generateDot = (frame) => {
    return `digraph{
      graph[splines=true];
      node[shape=record,style=filled,fillcolor=none];
      ${frame.nodes
        .map(
          (node) =>
            `${node.index}[fillcolor=${node.color ?? "white"}][label="{${
              node.index +
              Object.entries(node.tags)
                .map(([k, v]) => k + " = " + v)
                .reduce((s, t) => s + " | " + t, "")
            }}"];`,
        )
        .reduce((s, t) => s + t, "")}
      ${frame.edges
        .map(
          (edge) =>
            `${edge.u} -> ${edge.v}[dir=${
              edge.directed ? "forward" : "none"
            },label="${edge.label ?? ""}"];`,
        )
        .reduce((s, t) => s + t, "")}
    }`;
  };

  const generateFrames = (input) => {
    let ret = [];
    let frame = {
      nodes: [],
      edges: [],
      lineNo: 0,
      spots: {},
    };
    let getNode = (index) => {
      return frame.nodes.find((node) => node.index === index);
    };
    input.split("\n").forEach((line) => {
      if (line === "") return;
      let cmd = line.split(" ");
      switch (cmd[1]) {
        case "add_node":
          frame.nodes.push({ index: cmd[2], tags: {} });
          break;
        case "add_edge":
          frame.edges.push({
            u: cmd[2],
            v: cmd[3],
            directed: cmd[4] === "1" ? true : false,
          });
          break;
        case "set_node_color":
          frame.nodes.forEach((node) => {
            if (node.index === cmd[2]) {
              node.color = cmd[3];
            }
          });
          break;
        case "set_edge_label":
          frame.edges.forEach((edge) => {
            if (edge.u === cmd[2] && edge.v === cmd[3]) {
              edge.label = cmd[4];
            }
          });
          break;
        case "update":
          frame.lineNo = parseInt(cmd[0]);
          frame.dot = generateDot(frame);
          ret.push(structuredClone(frame));
          break;
        case "spot_node":
          if (frame.spots[cmd[3]]) {
            getNode(frame.spots[cmd[3]]).color = undefined;
          }
          frame.spots[cmd[3]] = cmd[2];
          getNode(cmd[2]).color = cmd[3];
          break;
        case "unspot_node":
          if (frame.spots[cmd[2]]) {
            getNode(frame.spots[cmd[2]]).color = undefined;
            delete frame.spots[cmd[2]];
          }
          break;
        case "set_node_tag":
          frame.nodes.forEach((node) => {
            if (node.index === cmd[2]) {
              node.tags[cmd[3]] = cmd[4];
            }
          });
          break;
        default:
          throw SyntaxError(`Command not found:${line}`);
      }
    });
    console.log(ret);
    return ret;
  };
  const renderDot = () => {
    // console.log("rendering: "+ frames[realPageNo-1].dot)
    if (decoration) {
      decoration.clear();
    }
    decoration = codeEditorRef.current.createDecorationsCollection([
      {
        range: new monacoRef.current.Range(
          frames[realPageNo - 1].lineNo,
          0,
          frames[realPageNo - 1].lineNo,
          0,
        ),
        options: {
          className: "highlightLine",
          isWholeLine: true,
        },
      },
    ]);
    codeEditorRef.current.revealLineInCenter(frames[realPageNo - 1].lineNo);
    graphviz("#canvas")
      .transition(d3.transition().duration(100).ease(d3.easeLinear))
      .renderDot(frames[realPageNo - 1].dot);
    // d3.select("#canvas").graphviz().transition(d3.transition().duration(750).ease(d3.easeLinear)).renderDot('graph{1;2;1--2;}').width('100%').height('100%');
  };

  const onGenerateFramesClick = () => {
    frames = generateFrames(graph);
    setPageCount(frames.length);
    setPageNo(1);
    realPageNo = 1;
    renderDot();
  };
  const goLeft = () => {
    if (pageNo > 1) {
      setPageNo(pageNo - 1);
      realPageNo--;
      renderDot();
    }
  };
  const goRight = () => {
    if (pageNo < pageCount) {
      setPageNo(pageNo + 1);
      realPageNo++;
      renderDot();
    }
  };
  const a11yProps = (index) => {
    return {
      id: `right-tab-${index}`,
      "aria-controls": `right-tabpanel-${index}`,
    };
  };
  const switchTab = (e, v) => {
    setTabNo(v);
  };

  return (
    <>
      <script
        src="https://unpkg.com/@hpcc-js/wasm@2.22.4/dist/graphviz.umd.js"
        type="javascript/worker"
      ></script>
      <Box sx={{ flexGrow: 1 }}>
        <AppBar position="static">
          <Toolbar>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Playground
            </Typography>
          </Toolbar>
        </AppBar>
      </Box>
      <Grid
        container
        width="100%"
        spacing="3"
        height="calc(100vh - 64px)"
        style={{ display: "flex" }}
      >
        <Grid
          item
          xs={6}
          style={{ flex: "1 1 auto", display: "flex", height: "100%" }}
        >
          <Paper
            style={{ flex: "1 1 auto", display: "flex", flexFlow: "column" }}
          >
            <div style={{ flex: "1 1 auto", overflow: "auto" }}>
              <Editor
                automaticLayout="true"
                defaultLanguage="cpp"
                defaultValue={defaultCode}
                onMount={(e, m) => {
                  codeEditorRef.current = e;
                  monacoRef.current = m;
                }}
                style={{ flex: 1 }}
                options={{ minimap: { enabled: false } }}
              ></Editor>
              {inputPanelOpen || <Toolbox></Toolbox>}
            </div>
            <LoadingButton
              loadingPosition="start"
              loading={running}
              onClick={runCode}
              startIcon={<PlayArrow />}
              variant="outlined"
              color="success"
              style={{ position: "absolute", right: "52%", marginTop: "8px" }}
            >
              运行
            </LoadingButton>
            <Button
              onClick={() => {
                setInputPanelOpen(!inputPanelOpen);
              }}
              variant="outlined"
              endIcon={inputPanelOpen ? <ExpandMore /> : <ExpandLess />}
            >
              输入/输出
            </Button>
            <div>
              <Box hidden={!inputPanelOpen}>
                <Grid container width="100%">
                  <Grid item xs={6}>
                    <p>标准输入</p>
                    <Editor
                      height="20vh"
                      defaultLanguage="text"
                      defaultValue="2 aa ab"
                      onMount={(e) => (inputEditorRef.current = e)}
                      options={{ minimap: { enabled: false } }}
                    ></Editor>
                  </Grid>
                  <Grid item xs={6}>
                    <p>标准输出</p>
                    <Editor
                      height="20vh"
                      defaultLanguage="text"
                      onMount={(e) => (stdoutEditorRef.current = e)}
                      options={{ minimap: { enabled: false }, readOnly: true }}
                    ></Editor>
                  </Grid>
                </Grid>
              </Box>
            </div>
          </Paper>
        </Grid>
        <Grid item xs={6} style={{ display: "flex", flexFlow: "column" }}>
          <Paper style={{ flex: 1, display: "flex", flexFlow: "column" }}>
            <Box
              sx={{ width: "100%" }}
              style={{ flex: 1, display: "flex", flexFlow: "column" }}
            >
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                  value={tabNo}
                  onChange={switchTab}
                  aria-label="right tabs"
                >
                  <Tab label="编译输出" {...a11yProps(0)} />
                  <Tab label="可视化" {...a11yProps(1)} />
                </Tabs>
              </Box>
              <RightTabPanel value={tabNo} index={0}>
                <div ref={terminalRef} style={{ flexGrow: 1 }}></div>
              </RightTabPanel>
              <RightTabPanel value={tabNo} index={1}>
                <div id="canvas" style={{ flexGrow: 1 }}></div>
                <Tooltip
                  title="左键拖拽移动，滚轮缩放"
                  style={{
                    position: "absolute",
                    right: 0,
                    marginTop: 8,
                    marginRight: 8,
                  }}
                >
                  <IconButton>
                    <HelpOutline />
                  </IconButton>
                </Tooltip>
                <div style={{ textAlign: "center" }}>
                  <Button onClick={goLeft}>
                    <ChevronLeft></ChevronLeft>
                  </Button>
                  {pageNo.toString()} / {pageCount.toString()}
                  <Button onClick={goRight}>
                    <ChevronRight></ChevronRight>
                  </Button>
                </div>
              </RightTabPanel>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </>
  );
}

export default App;
